<template>
  <div
    class="ads-controls"
    role="toolbar"
    :aria-label="t('player.ads.advertisement')"
  >
    <div class="ads-controls__left">
      <span class="ads-controls__badge">{{ t('player.ads.advertisement') }}</span>

      <span
        v-if="adsManager.totalAdsInBreak.value > 1"
        class="ads-controls__counter"
      >
        {{ t('player.ads.adCounter', {
          current: adsManager.currentAdIndex.value + 1,
          total: adsManager.totalAdsInBreak.value,
        }) }}
      </span>

      <span
        class="ads-controls__countdown"
        role="timer"
        :aria-label="formattedTimeRemaining"
      >
        {{ formattedTimeRemaining }}
      </span>
    </div>

    <!-- Skip button — positioned above the control bar -->
    <div class="ads-controls__skip-area" aria-live="polite">
      <button
        v-if="adsManager.isAdSkippable.value || adsManager.currentAd.value?.skipOffset !== undefined"
        ref="skipButtonRef"
        class="ads-controls__skip-button"
        :class="{ 'ads-controls__skip-button--active': adsManager.isAdSkippable.value }"
        type="button"
        :disabled="!adsManager.isAdSkippable.value"
        :aria-label="adsManager.isAdSkippable.value ? t('player.ads.skipAd') : t('player.ads.skipIn', { seconds: skipCountdown })"
        @click="adsManager.skipAd()"
      >
        {{ adsManager.isAdSkippable.value ? t('player.ads.skipAd') : t('player.ads.skipIn', { seconds: skipCountdown }) }}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
        </svg>
      </button>
    </div>

    <div class="ads-controls__right">
      <!-- Mute toggle -->
      <button
        class="ads-controls__mute-button"
        type="button"
        :aria-label="isMuted ? t('player.controls.unmute') : t('player.controls.mute')"
        @click="toggleMute"
      >
        <svg
          v-if="isMuted"
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
        </svg>
        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
        </svg>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, nextTick, ref, watch } from 'vue'
  import type { UseAdsManagerReturn } from '../../composables/useAdsManager'
  import { useVideoI18n } from '../../i18n'

  const props = defineProps<{
    adsManager: UseAdsManagerReturn
    isMuted: boolean
  }>()

  const emit = defineEmits<{
    toggleMute: []
  }>()

  const { t } = useVideoI18n()

  const skipButtonRef = ref<HTMLButtonElement | null>(null)

  watch(() => props.adsManager.isAdSkippable.value, async (skippable) => {
    if (skippable) {
      await nextTick()
      skipButtonRef.value?.focus()
    }
  })

  const formattedTimeRemaining = computed(() => {
    const total = Math.ceil(props.adsManager.adTimeRemaining.value)
    const mins = Math.floor(total / 60)
    const secs = total % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  })

  const skipCountdown = computed(() => {
    const ad = props.adsManager.currentAd.value
    if (!ad?.skipOffset) return 0
    return Math.max(0, Math.ceil(ad.skipOffset - props.adsManager.adCurrentTime.value))
  })

  function toggleMute(): void {
    emit('toggleMute')
  }
</script>

<style scoped lang="scss">
  .ads-controls {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 12;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.75rem 1rem;
    background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
    // The bar spans the ad's full width, so it lets clicks through to the
    // click-through overlay beneath it; only its buttons take them.
    pointer-events: none;
    color: #fff;

    &__left {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    &__right {
      display: flex;
      align-items: center;
      gap: 0.75rem;
    }

    &__badge {
      font-size: 0.75rem;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      background: rgba(255, 255, 255, 0.2);
      padding: 0.125rem 0.5rem;
      border-radius: 2px;
    }

    &__counter {
      font-size: 0.8125rem;
      opacity: 0.8;
    }

    &__countdown {
      font-size: 0.8125rem;
      font-variant-numeric: tabular-nums;
      opacity: 0.8;
    }

    &__mute-button {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 2rem;
      height: 2rem;
      padding: 0;
      border: none;
      border-radius: 4px;
      background: transparent;
      color: inherit;
      cursor: pointer;
      pointer-events: auto;
      transition: background-color 0.15s ease;

      &:hover {
        background: rgba(255, 255, 255, 0.15);
      }

      &:focus-visible {
        outline: 2px solid #fff;
        outline-offset: 2px;
      }
    }

    &__skip-area {
      position: absolute;
      bottom: 100%;
      right: 1rem;
      margin-bottom: 0.5rem;
      display: flex;
      align-items: center;
      pointer-events: auto;
    }

    &__skip-button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 0.375rem;
      min-width: 200px;
      font-size: 1rem;
      font-weight: 600;
      padding: 0.625rem 1.25rem;
      border: none;
      border-radius: 6px;
      color: #fff;
      transition: background-color 0.3s ease, box-shadow 0.3s ease;

      &:disabled {
        background: rgba(0, 0, 0, 0.6);
        border: 1px solid rgba(255, 255, 255, 0.25);
        cursor: default;
        font-weight: 500;
      }

      &--active {
        background: var(--vp-primary-color, #884aff);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
        cursor: pointer;

        &:hover {
          opacity: 0.85;
        }

        &:focus-visible {
          outline: 2px solid #fff;
          outline-offset: 2px;
        }
      }
    }
  }

  @media (max-width: 768px) {
    .ads-controls {
      padding: 0.5rem 0.75rem;

      &__badge {
        font-size: 0.75rem;
      }

      &__counter,
      &__countdown {
        font-size: 0.75rem;
      }
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .ads-controls__skip-button,
    .ads-controls__mute-button {
      transition: none;
    }
  }
</style>
